// Vuelta del TPV: un único punto de entrada para TODO lo que puede dejar
// a un camarero con el estado desfasado (móvil bloqueado, segundo plano,
// avión, navegador congelado o restaurado desde bfcache, hub reconectado,
// API reiniciada).
//
// Flujo de cada vuelta (una a la vez, con UNA repetición encolada si llega
// otro disparador mientras corre — ver resumeOrchestrator):
//   1. Sin red → chip "Sin conexión" y nada más (el evento `online` vuelve).
//   2. Fuera mucho / congelado → reinicia los sockets "Connected" (pueden ser
//      zombies) y patea los caídos. Al reconectar llega otro disparador
//      ("reconnected") que queda encolado y cierra el hueco del reinicio.
//   3. Catch-up del tiempo real (V2): `realtime/seq` → reenvío
//      `realtime/sync?since=` por los MISMOS handlers que en directo (y al
//      puente legacy) o, si el servidor ya no tiene el hueco / epoch nuevo,
//      estado completo.
//   4. Lo que no viaja por la secuencia (plan de `resumePlan`): presencia,
//      lease (re-adquirir o takeover), add-ons, datos del restaurante, KDS…
//   5. Si algo esencial falla → chip "Reconectando…" y reintento con backoff.
import { createResumeOrchestrator, attachResumeListeners } from "./resumeOrchestrator";
import { planResumeRefresh } from "./resumePlan";
import { runResumeRefresh, bindTpvResyncStore } from "src/utils/tpvResync";
import { refreshStaleConnections } from "src/utils/hubs/HubConnectionManager";
import { PATH_APP } from "src/routes/paths";
import { selectedMesaSnapshot, shouldLeaveClosedMesa } from "./selectedMesaClosedCore";

/** Fuera más que esto → los sockets pueden ser zombies: se reinician. */
export const HUB_RESTART_AFTER_MS = 20_000;
const RETRY_BASE_MS = 3_000;
const RETRY_MAX_MS = 30_000;
const LONG_AWAY_REASONS = ["freeze", "pageshow", "resume"];

// ── Estado de salud (chip "Reconectando…" / "Sin conexión") ──────────────
// `lastCaughtUpAt`: fin de la última vuelta que terminó bien (el banner de
// tiempo real solo pasa a verde «datos actualizados» tras una posterior al corte).
let health = { status: "ok", since: null, lastOkAt: null, lastCaughtUpAt: null };
const healthListeners = new Set();

function notifyHealth() {
  healthListeners.forEach((fn) => {
    try {
      fn(health);
    } catch {
      /* ignore */
    }
  });
}

function setHealth(status) {
  if (health.status === status) return;
  health = {
    ...health,
    status,
    since: status === "ok" ? null : Date.now(),
    lastOkAt: status === "ok" ? Date.now() : health.lastOkAt,
  };
  notifyHealth();
}

function markCaughtUp() {
  const now = Date.now();
  health = { ...health, status: "ok", since: null, lastOkAt: now, lastCaughtUpAt: now };
  notifyHealth();
}

export const getResumeHealth = () => health;
export function subscribeResumeHealth(fn) {
  healthListeners.add(fn);
  return () => healthListeners.delete(fn);
}

// ── Catch-up del tiempo real (lo registra useTpvRealtime en V2) ───────────
let catchUp = null;

/**
 * `fn()` → Promise<{ mode: "none"|"replay"|"snapshot", catalog?: boolean }>;
 * lanza si no pudo ni preguntar al servidor.
 */
/** ¿Hay catch-up por secuencia (V2)? En legacy los hooks se resincronizan solos. */
export const hasRealtimeCatchUp = () => catchUp != null;

export function registerRealtimeCatchUp(fn) {
  catchUp = fn;
  return () => {
    if (catchUp === fn) catchUp = null;
  };
}

// ── Vuelta ────────────────────────────────────────────────────────────────
let storeRef = null;
let resumeHistory = null;
/** Router de la app (lo fija useTpvResume) para volver al plano. */
export function setResumeHistory(h) {
  resumeHistory = h || null;
}
let retryTimer = null;
let retryAttempt = 0;

const isOffline = () => typeof navigator !== "undefined" && navigator.onLine === false;
const currentPath = () => (typeof window !== "undefined" ? window.location?.pathname || "" : "");

function scheduleRetry() {
  if (retryTimer) return;
  const ms = Math.min(RETRY_MAX_MS, RETRY_BASE_MS * 2 ** retryAttempt);
  retryAttempt += 1;
  retryTimer = setTimeout(() => {
    retryTimer = null;
    orchestrator.trigger("retry");
  }, ms);
}

function clearRetry() {
  retryAttempt = 0;
  if (retryTimer) clearTimeout(retryTimer);
  retryTimer = null;
}

export async function runResume({ reasons = [], awayMs = 0 } = {}) {
  if (!storeRef) return { skipped: true };
  if (isOffline()) {
    setHealth("offline");
    return { offline: true };
  }
  const onlyReconnect = reasons.length > 0 && reasons.every((r) => r === "reconnected" || r === "retry");
  const longAway = awayMs >= HUB_RESTART_AFTER_MS || reasons.some((r) => LONG_AWAY_REASONS.includes(r));
  if (longAway && !onlyReconnect) refreshStaleConnections(reasons.join(","));

  // Legacy (sin secuencia): los hooks legacy ya hacen su resync al
  // reconectar; para el resto de motivos, estado completo.
  if (!catchUp && onlyReconnect && reasons.includes("reconnected")) {
    clearRetry();
    markCaughtUp();
    return { mode: "legacy-reconnect" };
  }

  // Mesa abierta antes de la vuelta (ver selectedMesaClosed).
  const selectedBefore = selectedMesaSnapshot(storeRef.getState());

  let mode = "snapshot";
  let catalog = false;
  if (catchUp) {
    try {
      const r = await catchUp();
      mode = r?.mode || "snapshot";
      catalog = !!r?.catalog;
    } catch {
      mode = "failed";
    }
  }

  const keys = planResumeRefresh({
    mode,
    awayMs,
    catalog,
    path: currentPath(),
    state: storeRef.getState(),
    tpvPath: PATH_APP?.tuCarta?.tpv || "",
  });
  const res = await runResumeRefresh(keys);
  // Estado completo (fuera > 60 min, API reiniciada…): el plano nuevo puede
  // traer libre la mesa en la que estaba → salir con aviso (con el reenvío
  // de eventos ya lo hizo el handler de mesa.changed y aquí no queda nada).
  try {
    if (shouldLeaveClosedMesa(selectedBefore, storeRef.getState())) {
      const { leaveClosedMesa } = await import("./selectedMesaClosed");
      leaveClosedMesa({
        dispatch: storeRef.dispatch,
        getState: storeRef.getState,
        history: resumeHistory,
        user: storeRef.getState()?.authJwt?.user,
        mesaId: selectedBefore.mesaId,
      });
    }
  } catch {
    /* nunca rompe la vuelta */
  }

  if (mode === "failed" || isOffline()) {
    setHealth(isOffline() ? "offline" : "reconnecting");
    scheduleRetry();
  } else {
    clearRetry();
    markCaughtUp();
  }
  return { mode, keys, failed: res?.failed || [] };
}

const orchestrator = createResumeOrchestrator({ run: runResume });

/** Pide una vuelta (debounce + una a la vez). */
export function requestResume(reason, opts) {
  orchestrator.trigger(reason, opts);
}

export const whenResumeIdle = () => orchestrator.whenIdle();

// Montado por TpvRealtimeHubs (TPV grande, carta camarero, panel). Con
// recuento: los dos layouts pueden solaparse en una transición.
let mounts = 0;
let detach = null;
let detachOffline = null;

export function mountTpvResume(dispatch, getState) {
  storeRef = { dispatch, getState };
  bindTpvResyncStore(dispatch, getState);
  mounts += 1;
  if (mounts === 1) {
    detach = attachResumeListeners(orchestrator);
    if (typeof window !== "undefined") {
      const onOffline = () => setHealth("offline");
      window.addEventListener("offline", onOffline);
      detachOffline = () => window.removeEventListener("offline", onOffline);
    }
    if (isOffline()) setHealth("offline");
  }
  return () => {
    mounts -= 1;
    if (mounts > 0) return;
    detach?.();
    detachOffline?.();
    detach = null;
    detachOffline = null;
    clearRetry();
  };
}

/** Solo para tests. */
export function __resetTpvResumeForTests() {
  storeRef = null;
  catchUp = null;
  mounts = 0;
  detach?.();
  detach = null;
  detachOffline?.();
  detachOffline = null;
  clearRetry();
  health = { status: "ok", since: null, lastOkAt: null, lastCaughtUpAt: null };
  healthListeners.clear();
}
